import { GetHighlightsResponse } from "@financial-advisory/data-access/api-types";
import { ProcessedClause } from "@financial-advisory/data-access/api-types";
import { IHighlight } from "@financial-advisory/ui/components/pdf-viewer";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { ProcessedClauseApi } from "./decisionProcesses";
import { selectClauseById } from "./decisionProcessesSlice";

// Define a type for the slice state
interface HighlightsSliceState {
  data: Record<string, IHighlight>;
  processedClauseIdByHighlightId: Record<string, string>;
}

// Define the initial state using that type
const initialState: HighlightsSliceState = {
  data: {},
  processedClauseIdByHighlightId: {},
};

export const highlightsSlice = createSlice({
  name: "highlights",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        ProcessedClauseApi.endpoints.getHighlights.matchFulfilled,
        (state, action) => {
          const response: GetHighlightsResponse = action.payload;
          state.data = response.data.reduce((acc, curr) => {
            return {
              ...acc,
              [curr.id]: curr,
            };
          }, {});
        }
      )
      .addMatcher(
        ProcessedClauseApi.endpoints.getProcessedClauses.matchFulfilled,
        (state, action) => {
          const response: { data: ProcessedClause[] } = action.payload;
          response.data.forEach((clause) => {
            clause.highlights.forEach((highlight) => {
              state.processedClauseIdByHighlightId[highlight.id] = clause.id;
            });
          });
        }
      );
  },
});

export const selectHighlightsList = (state: RootState) =>
  Object.values(state.highlights.data);

export const selectHighlightById = (id: string) => (state: RootState) =>
  state.highlights.data[id];

export const selectClauseByHighlightId =
  (highlightId: string) =>
  (state: RootState): ProcessedClause | undefined => {
    const processedClauseId =
      state.highlights.processedClauseIdByHighlightId[highlightId];
    if (!processedClauseId) return undefined;
    const clause = selectClauseById(processedClauseId)(state);
    return clause;
  };
